import styles from "../../styles/addReceipt.module.css";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { showRestanteError } from "./Alerts";
import apiFetch from "../../../../../shared/services/apiClient";

const PAGE_SIZE = 10;

export function ReceiptFooter({
  selectedFilial,
  setSelectedFilial,
  setFacturasData,
  facturasData,
  receiptData,
  setReceiptData,
  setDisplayValueReceipt,
  restante,
  onCreated,
  validate,
  clearErrors,
  errors = {},
  montoEsValido,
  reloadCxc, // fuerza recarga de CxC tras guardar
  soloSaldoFavor,      // true cuando monto=0 y solo se aplica saldo a favor
  referenciaAutoSaldo, // referencia automática del recibo de origen
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const submittingRef = useRef(false);

  async function crearRecibo() {
    // Si es solo aplicación de saldo a favor (monto=0),
    // usar la referencia automática del recibo origen como documento.
    const documentoFinal = soloSaldoFavor
      ? (referenciaAutoSaldo || "Aplicación saldo a favor")
      : receiptData.documento;
    const res = await apiFetch("/api/finance/recibo/", {
      method: "POST",
      body: JSON.stringify({ ...receiptData, filial: selectedFilial, documento: documentoFinal }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Error al crear recibo");
    }
    return res.json();
  }

  async function crearDetallesRecibo(reciboId) {
    const aplicadas = facturasData.filter((f) => f.checked && f.abono !== 0);
    for (const f of aplicadas) {
      const res = await apiFetch("/api/finance/reciboDetalle/", {
        method: "POST",
        body: JSON.stringify({
          recibo: reciboId,
          factura: f.factura.id_factura,
          abono: f.abono,
          tratar_como_diferencial: f.tratar_como_diferencial ?? false,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const isDup =
          res.status === 400 && (JSON.stringify(err).includes("unique") || JSON.stringify(err).includes("único"));
        if (isDup) continue;
        throw new Error(
          `Error en detalle factura ${f.factura.numero_factura}: ${err.detail || "error"}`,
        );
      }
    }
  }

  async function handleSubmit() {
    setApiError("");

    // Validación 1: campos de formulario
    // Pasar montoEsValido a validate() para que sepa cuándo monto=0 es aceptable
    if (validate) {
      const valid = validate(montoEsValido, soloSaldoFavor);
      if (!valid) return;
    }

    // Validación 2: el restante debe ser 0 para guardar (tolerancia de 0.05 por redondeo)
    if (Math.abs(restante) > 0.05) {
      showRestanteError().fire();
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      // Limpiar formulario y resetear estado del botón

      const recibo = await crearRecibo();
      const aplicadas = facturasData.filter((f) => f.checked && f.abono !== 0);

      if (aplicadas.length === 0) {
        // Sin facturas: redirigir a aplicar
        navigate(`/finanzas/recibos/${recibo.id}/aplicar`);
        return;
      }

      await crearDetallesRecibo(recibo.id);
      handleReset();

      if (clearErrors) clearErrors();
      setSubmitting(false); // ← desbloquear botón tras éxito

      // Recargar CxC para que estén listas para el próximo recibo
      if (reloadCxc) reloadCxc();

      // Notificar al padre (AddReceipt) con el recibo creado para ofrecer el correo
      if (onCreated) {
        onCreated(recibo);
      } else {
        navigate("/finanzas/recibos");
      }
    } catch (err) {
      console.error("Error creando recibo:", err);
      setApiError(err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function handleReset() {
    setFacturasData((prev) =>
      prev.map((inv) => ({ ...inv, abono: 0, favor: 0, checked: false })),
    );
    setSelectedFilial(0);
    setReceiptData({
      monto: 0,
      cuenta_bancaria: 0,
      metodo_pago: "TRANSFERENCIA",
      documento: "",
      descripcion: "",
      fecha_pago: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0],
      moneda_pago: "CRC",
      tipo_cambio: 1,
    });
    setDisplayValueReceipt("");
    submittingRef.current = false;
  }

  const errorCount = Object.keys(errors).length;

  return (
    <div className={styles["receipt-footer"]}>
      {/* Resumen de errores de validación */}
      {errorCount > 0 && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#f87171",
              fontWeight: 600,
              marginBottom: errorCount > 1 ? 6 : 0,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation" />
            {errorCount === 1
              ? Object.values(errors)[0]
              : `Hay ${errorCount} campos con error:`}
          </div>
          {errorCount > 1 && (
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                color: "#fca5a5",
                fontSize: "0.83rem",
              }}
            >
              {Object.values(errors).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Error de API */}
      {apiError && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "10px 16px",
            marginBottom: 12,
            color: "#f87171",
            fontSize: "0.87rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <i className="fa-solid fa-circle-exclamation" />
          {apiError}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles["btn-clean-form"]}`}
          onClick={handleReset}
          disabled={submitting}
        >
          <i className="fa-solid fa-repeat" /> Limpiar Formulario
        </button>
        <button
          className={`${styles.btn} ${styles["btn-save"]}`}
          onClick={handleSubmit}
          disabled={submitting}
          style={{ opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              &nbsp;Guardando…
            </>
          ) : (
            <>
              <i className="fa-solid fa-save" /> Guardar Recibo
            </>
          )}
        </button>
      </div>
    </div>
  );
}
